import React, { useEffect, useState, useMemo } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

interface Bookmark {
  id: number;
  company_id: number;
  name: string;
  url: string | null;
  parent_id: number | null;
  type: 'bookmark' | 'folder';
  is_public: number;
  created_at: string;
  updated_at: string;
}

interface BookmarksProps {
  companyId?: string; // Optional companyId prop for company page context
}

const Bookmarks: React.FC<BookmarksProps> = ({ companyId: propCompanyId }) => {
  const { user } = useAuth();
  // Use prop companyId if provided (from company page), otherwise use user's company_id
  const companyId = propCompanyId || user?.company_id?.toString();
  
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBookmark, setEditingBookmark] = useState<Bookmark | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    parent_id: '' as string | number,
    type: 'bookmark' as 'bookmark' | 'folder',
    is_public: false
  });

  const isGlobalUser = !user?.company_id;

  useEffect(() => {
    if (companyId) {
      fetchBookmarks();
    }
  }, [companyId]);

  const fetchBookmarks = async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      const res = await api.get(`/companies/${companyId}/bookmarks`);
      setBookmarks(res.data);
    } catch (error) {
      console.error('Error fetching bookmarks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        parent_id: formData.parent_id === '' ? null : Number(formData.parent_id),
        url: formData.type === 'folder' ? null : formData.url,
        is_public: formData.is_public ? 1 : 0
      };

      if (editingBookmark) {
        await api.put(`/companies/${companyId}/bookmarks/${editingBookmark.id}`, data);
      } else {
        await api.post(`/companies/${companyId}/bookmarks`, data);
      }
      setShowModal(false);
      setEditingBookmark(null);
      setFormData({ name: '', url: '', parent_id: '', type: 'bookmark', is_public: false });
      fetchBookmarks();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error saving bookmark');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this bookmark/folder? All nested items will also be deleted.')) {
      try {
        await api.delete(`/companies/${companyId}/bookmarks/${id}`);
        fetchBookmarks();
      } catch (error) {
        alert('Error deleting bookmark');
      }
    }
  };

  const openAddModal = (type: 'bookmark' | 'folder', parentId: number | null = null) => {
    setEditingBookmark(null);
    setFormData({
      name: '',
      url: '',
      parent_id: parentId === null ? '' : parentId,
      type,
      is_public: false
    });
    setShowModal(true);
  };

  const openEditModal = (bookmark: Bookmark) => {
    setEditingBookmark(bookmark);
    setFormData({
      name: bookmark.name,
      url: bookmark.url || '',
      parent_id: bookmark.parent_id === null ? '' : bookmark.parent_id,
      type: bookmark.type,
      is_public: bookmark.is_public === 1
    });
    setShowModal(true);
  };

  // Build tree structure
  const renderBookmarkTree = (parentId: number | null = null, depth = 0) => {
    const items = bookmarks.filter(b => b.parent_id === parentId);
    
    if (items.length === 0 && parentId !== null) return null;

    return (
      <ul className={`${depth > 0 ? 'ml-6 border-l border-gray-200 dark:border-gray-700' : ''}`}>
        {items.map(item => (
          <li key={item.id} className="py-2 pl-4">
            <div className="flex items-center justify-between group">
              <div className="flex items-center space-x-2">
                {item.type === 'folder' ? (
                  <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" />
                  </svg>
                )}
                {item.type === 'bookmark' ? (
                  <a
                    href={item.url || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-brand dark:hover:text-brand"
                  >
                    {item.name}
                  </a>
                ) : (
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{item.name}</span>
                )}
                {isGlobalUser && (
                  <span className={`text-[10px] px-1 rounded ${item.is_public ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400'}`}>
                    {item.is_public ? 'Public' : 'Private'}
                  </span>
                )}
              </div>
              
              {isGlobalUser && (
                <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  {item.type === 'folder' && (
                    <button
                      onClick={() => openAddModal('bookmark', item.id)}
                      className="p-1 text-gray-400 hover:text-brand"
                      title="Add Bookmark to this folder"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </button>
                  )}
                  {item.type === 'folder' && (
                    <button
                      onClick={() => openAddModal('folder', item.id)}
                      className="p-1 text-gray-400 hover:text-yellow-600"
                      title="Add Subfolder"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                      </svg>
                    </button>
                  )}
                  <button
                    onClick={() => openEditModal(item)}
                    className="p-1 text-gray-400 hover:text-blue-600"
                    title="Edit"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="p-1 text-gray-400 hover:text-red-600"
                    title="Delete"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
            {item.type === 'folder' && renderBookmarkTree(item.id, depth + 1)}
          </li>
        ))}
      </ul>
    );
  };

  if (!companyId) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">You must be associated with a company to view bookmarks.</p>
        </div>
      </div>
    );
  }

  if (loading && bookmarks.length === 0) return <div className="text-center py-8">Loading bookmarks...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Bookmarks & Links</h2>
        {isGlobalUser && (
          <div className="flex space-x-2">
            <button
              onClick={() => openAddModal('folder')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md shadow-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              <svg className="w-4 h-4 mr-2 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
              </svg>
              New Folder
            </button>
            <button
              onClick={() => openAddModal('bookmark')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-brand hover:bg-brand-hover"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Bookmark
            </button>
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg p-6">
        {bookmarks.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
            </svg>
            <p className="mt-2 text-sm">No bookmarks yet.</p>
            {isGlobalUser && <p className="mt-1 text-xs">Click the buttons above to get started.</p>}
          </div>
        ) : (
          renderBookmarkTree(null)
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed z-20 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={() => setShowModal(false)}></div>
            </div>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 mb-4">
                    {editingBookmark ? 'Edit' : 'Add'} {formData.type === 'folder' ? 'Folder' : 'Bookmark'}
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
                      <input
                        type="text"
                        required
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      />
                    </div>
                    {formData.type === 'bookmark' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">URL</label>
                        <input
                          type="url"
                          required
                          placeholder="https://example.com"
                          className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          value={formData.url}
                          onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Parent Folder</label>
                      <select
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        value={formData.parent_id}
                        onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
                      >
                        <option value="">Root</option>
                        {bookmarks
                          .filter(b => b.type === 'folder' && b.id !== editingBookmark?.id)
                          .map(folder => (
                            <option key={folder.id} value={folder.id}>{folder.name}</option>
                          ))
                        }
                      </select>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="is_public"
                        type="checkbox"
                        className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded"
                        checked={formData.is_public}
                        onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
                      />
                      <label htmlFor="is_public" className="ml-2 block text-sm text-gray-900 dark:text-gray-100">
                        Public (Visible to Customers)
                      </label>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button type="submit" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-brand text-base font-medium text-white hover:bg-brand-hover sm:ml-3 sm:w-auto sm:text-sm">
                    Save
                  </button>
                  <button type="button" onClick={() => setShowModal(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-600 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bookmarks;
